import { GridApi } from 'ag-grid-community';
import { ContainerElement, ContainerEvent } from '@benzinga/containers';
import { Subscribable } from '@benzinga/subscribable';

interface OverlayEvent {
  type: 'grid_transaction:overlay';
  overlay: 'hideOverlay' | 'showLoadingOverlay' | 'showNoRowsOverlay';
}

interface OverlayErrorEvent {
  type: 'grid_transaction:overlay';
  overlay: 'showErrorOverlay';
  error: string;
}

interface DataEvent {
  type: 'grid_transaction:data';
  dataType: 'clearing' | 'flush';
}

type GridTransactionEvent = OverlayEvent | OverlayErrorEvent | DataEvent;

export class GridTransaction<
  T extends Partial<Record<keyof T, T[keyof T]>>,
> extends Subscribable<GridTransactionEvent> {
  private preReadyTransaction: ContainerElement<T>[] = [];
  private gridApi?: GridApi;
  private clear = true;

  public setGridApi = (gridApi: GridApi) => {
    this.gridApi = gridApi;
    this.preReadyTransaction.forEach(transaction => this.applyTransactionAsync(transaction));
    this.preReadyTransaction = [];
  };

  public transaction = (event: ContainerEvent<T>) => {
    switch (event.type) {
      case 'queued':
      case 'update':
        if (this.clear === true) {
          this.dispatch({ overlay: 'hideOverlay', type: 'grid_transaction:overlay' });
          this.clear = false;
        }
        this.makeTransaction(event.transaction);
        break;
      case 'clear':
        this.dispatch({
          dataType: 'clearing',
          type: 'grid_transaction:data',
        });

        this.clear = true;
        this.makeTransaction(event.transaction);
        this.flush();
        this.dispatch({
          dataType: 'flush',
          type: 'grid_transaction:data',
        });
    }
  };

  public loading = () => {
    this.dispatch({ overlay: 'showLoadingOverlay', type: 'grid_transaction:overlay' });
  };

  public noData = () => {
    this.dispatch({ overlay: 'showNoRowsOverlay', type: 'grid_transaction:overlay' });
  };

  public error = (error: string) => {
    this.dispatch({ error, overlay: 'showErrorOverlay', type: 'grid_transaction:overlay' });
  };

  public hideOverlay = () => {
    this.dispatch({ overlay: 'hideOverlay', type: 'grid_transaction:overlay' });
  };

  public flush = () => this.gridApi?.flushAsyncTransactions();

  private makeTransaction = (transaction: ContainerElement<T>) => {
    if (this.gridApi) {
      this.applyTransactionAsync(transaction);
    } else {
      this.preReadyTransaction.push(transaction);
    }
  };

  private applyTransactionAsync = (transaction: ContainerElement<T>) => {
    this.gridApi?.applyTransactionAsync(transaction);
  };
}
